import {
  useMemo,
  cloneElement,
  createElement,
  isValidElement,
  type JSX,
  type ReactNode,
  type ReactElement,
  type ComponentType,
  type ComponentProps,
  type FunctionComponent,
} from 'react';

import type { ControllerProps, FieldPath, FieldValues } from 'react-hook-form';

import { Show } from '~/components/show';
import { Text } from '~/components/text';
import { Input } from '~/components/input';
import { HStack } from '~/components/stack';
import {
  FormItem,
  FormLabel,
  FormField,
  FormControl,
  FormMessage,
  FormDescription,
} from '~/components/form';

type TDefaultComponent =
  | ReactElement
  | ComponentType<TAny>
  | FunctionComponent<TAny>;

type TInputProps = ComponentProps<typeof Input>;

interface IFormInputProps<
  TComponent extends undefined | TDefaultComponent,
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends Omit<ControllerProps<TFieldValues, TName>, 'render'> {
  label?: string;
  required?: boolean;
  className?: string;
  hideError?: boolean;
  children?: ReactNode;
  keepFields?: string[];
  description?: string;
  labelDescription?: string;
  containerClassName?: string;
  onChangeEventName?: string; // TODO: need to extract from component props
  component?: TComponent | typeof Input;
  render?: ControllerProps<TFieldValues, TName>['render'];
  componentProps?: TComponent extends undefined
    ? TInputProps
    : TComponent extends ComponentType<TInputProps>
      ? TInputProps
      : TComponent extends FunctionComponent<TInputProps>
        ? TInputProps
        : TComponent extends ComponentType<infer P>
          ? P
          : TComponent extends FunctionComponent<infer P>
            ? P
            : TComponent extends keyof JSX.IntrinsicElements
              ? JSX.IntrinsicElements[TComponent]
              : TInputProps;
}

export function FormInput<
  TComponent extends undefined | TDefaultComponent,
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  label,
  render,
  control,
  children,
  className,
  keepFields,
  description,
  componentProps,
  required = false,
  labelDescription,
  onChangeEventName,
  hideError = false,
  component = Input,
  containerClassName,
  ...rest
}: IFormInputProps<TComponent, TFieldValues, TName>): JSX.Element {
  const renderFunc = useMemo(() => {
    if (render) {
      return render;
    }

    const defaultRender: ControllerProps<TFieldValues, TName>['render'] = ({
      field,
    }) => {
      const { onChange, ...restField } = field;

      const props = {
        ...(restField as TAny),
        ...componentProps,
        [onChangeEventName || 'onChange']: (data: TAny) => {
          onChange?.(data);
          ((componentProps || {}) as TAny)[onChangeEventName || 'onChange']?.(
            data,
          );
        },
      };

      if (component !== Input && keepFields) {
        props.keepFields = keepFields;
      }

      return (
        <FormItem className={containerClassName}>
          <Show when={label}>
            <HStack className='justify-between'>
              <FormLabel required={required}>{label}</FormLabel>
              <Show when={labelDescription}>
                <Text size='sm' className='text-dimGray-500'>
                  {labelDescription}
                </Text>
              </Show>
            </HStack>
          </Show>
          <HStack gap={4} className='w-full items-center'>
            <div className='w-full'>
              <FormControl>
                {isValidElement(component)
                  ? cloneElement(component, props)
                  : createElement(component, props)}
              </FormControl>
            </div>
            {children}
          </HStack>
          <Show when={description}>
            <FormDescription>{description}</FormDescription>
          </Show>
          <Show when={!hideError}>
            <FormMessage />
          </Show>
        </FormItem>
      );
    };

    return defaultRender;
  }, [
    children,
    component,
    componentProps,
    containerClassName,
    description,
    hideError,
    keepFields,
    label,
    labelDescription,
    onChangeEventName,
    render,
    required,
  ]);

  return (
    <div className={className}>
      <FormField name={name} control={control} render={renderFunc} {...rest} />
    </div>
  );
}
